import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { AlertService, IAlertType } from "./alert.service";
import { Observable, throwError } from "rxjs";
import {
  Companies,
  ek,
  JobDeleteImpact,
  JobPositionControlling,
  vk,
  Jobs,
  JobsList,
  ProjectsComment,
  WebserviceResponse,
  ekvk, Delivery, SingleJobResponse
} from "../interfaces";
import { catchError, map, retry } from "rxjs/operators";
import { restBaseUrl } from "../utils/rest-url";
import { normalizeSingletonResponse } from "../utils/response";

@Injectable({
  providedIn: 'root'
})
export class JobsStoreService {
  private readonly endpoint = restBaseUrl();
  private readonly httpOptions = { responseType: 'json' as const };

  constructor(
    private http: HttpClient,
    private alertDispatcher: AlertService) { }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.alertConfig.message = errorMessage;
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.DANGER;

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  public createJob(projectID:string, offerID: number):Observable<WebserviceResponse<any>>{
    return this.http.post<WebserviceResponse<any>>(
      `${this.endpoint}/jobs/add_jobs?project_id=${projectID}&offer_id=${offerID}`,
      { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteJob(projectID:string, jobID: number){
    return this.http.delete(
      `${this.endpoint}/jobs/delete_jobs?project_id=${projectID}&job_id=${jobID}`,
      { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getDeleteJobImpact(projectID: string, jobID: number): Observable<WebserviceResponse<JobDeleteImpact>> {
    const options = new HttpParams({
      fromObject: {
        project_id: projectID,
        job_id: jobID
      }
    });
    return this.http.get<WebserviceResponse<JobDeleteImpact>>(
      `${this.endpoint}/jobs/get_delete_jobs_impact`,
      { params: options }
    ).pipe(
      map((response) => normalizeSingletonResponse(response)),
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  public updateJob(j: Jobs, JobID: number) {
    return this.http.put(
      `${this.endpoint}/jobs/update_job`,
      j,
      { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateBudget(jid:number, pid: string, budget: number) {
    return this.http.put(
      `${this.endpoint}/jobs/update_budget?job_id=${jid}&project_id=${pid}&budget=${budget}`,
      { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateComment(jid: number, pid: string, comment: ProjectsComment) {
    return this.http.put(
      `${this.endpoint}/jobs/update_comment?job_id=${jid}&project_id=${pid}`,
      comment,
      { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getJobs(projectID: string, jobID: number, status: string): Observable<WebserviceResponse<Jobs[]>> {
    const options = new HttpParams({
      fromObject: {
        job_id: jobID,
        project_id: projectID,
        status: status
      }
    });
    return this.http.get<WebserviceResponse<Jobs[]>>(
        this.endpoint + '/jobs/get_jobs',
      { params: options }).
          pipe(retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  public getSingleJob(projectID: string, jobID: number): Observable<SingleJobResponse> {
    const options = new HttpParams({
      fromObject: {
        job_id: jobID,
        project_id: projectID
      }
    });
    return this.http.get<SingleJobResponse>(
        this.endpoint + '/jobs/get_single_job',
      { params: options }).pipe(
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  // Job Position Services

  public getJobsPositions(projectID: string, jobID: number, positionID: string, partID: string, serialNr: string): Observable<WebserviceResponse<JobsList[]>> {
    const options = new HttpParams({
      fromObject: {
        project_id: projectID,
        job_id: jobID,
        position: positionID,
        part: partID,
        // serial_nr: serialNr
      }
    });
    return this.http.get<WebserviceResponse<JobsList[]>>(
        this.endpoint + '/jobs/get_job_position',
      { params: options }).pipe(
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  public updateJobPosition(jobs: JobsList): Observable<WebserviceResponse<JobsList>> {
    return this.http.put<WebserviceResponse<JobsList>>(
      `${this.endpoint}/jobs/update_job_position`,
      jobs,
      this.httpOptions
    ).pipe(
      map((response) => normalizeSingletonResponse(response)),
      retry(3), // Versucht die Anfrage dreimal, bevor ein Fehler ausgegeben wird
      catchError(this.handleError)
    );
  }

  public deleteJobPosition(projectID: string, jobID: number, positionID: string, partID: number) {
    return this.http.delete(
      `${this.endpoint}/jobs/delete_job_position?project_id=${projectID}&job_id=${jobID}&position=${positionID}&part=${partID}`,
      { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public addSingleProjectPosition(projectID: string, jobID: number, positionID: number, partID: number) {
    return this.http.post(
      `${this.endpoint}/jobs/add_single_job_position?project_id=${projectID}&job_id=${jobID}&part=${partID}&position=${positionID}`,
      { responseType: 'text'}
    ).pipe(retry(3), catchError(this.handleError));
  }

  public getJobPositionControlling(projectID: string, jobID: number): Observable<WebserviceResponse<JobPositionControlling[]>> {
    const options = new HttpParams({
      fromObject: {
        project_id: projectID,
        job_id: jobID
      }
    });
    return this.http.get<WebserviceResponse<JobPositionControlling[]>>(
      this.endpoint + '/jobs/get_job_position_controlling',
      { params: options }
    ).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  public getSingleCompany(cid: string) : Observable<WebserviceResponse<Companies>> {
    const options = new HttpParams({
      fromObject: {
        contact_id: cid,
        count: '1'
      }
    });
    return this.http.get<WebserviceResponse<Companies>>(
        this.endpoint + '/jobs/get_contacts',
      { params: options }).pipe(
          map((response) => normalizeSingletonResponse(response)),
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  // Search Job by Serial Number

  public searchJobPositionBySerialNumber(serialNr: string): Observable<WebserviceResponse<JobsList[]>> {
    const options = new HttpParams({
      fromObject: {
        project_id: '',
        job_id: 0,
        position: 0,
        part: 0,
        serial_nr: serialNr
      }
    });
    return this.http.get<WebserviceResponse<JobsList[]>>(
        this.endpoint + '/jobs/get_job_position',
      { params: options }).pipe(
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  // Job Status

  public updateJobStatus(projectID: string, jobID: number, status: string){
    return this.http.put(
      `${this.endpoint}/jobs/update_status?project_id=${projectID}&job_id=${jobID}&status=${status}`,
      { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // How many pieces are delivered?

  public addPiecesDelivered(jobID: number, positions: Delivery[]){
    return this.http.put(
      `${this.endpoint}/delivery/add_pieces_delivered_from_delivery_position?job_id=${jobID}`,
      positions,
      { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public substractPiecesDelivered(jobID: number, positions: Delivery[]){
    return this.http.put(
      `${this.endpoint}/delivery/subtract_pieces_delivered_from_delivery_position?job_id=${jobID}`,
      positions,
      { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  private updateNumberOfPiecesDelivered(projectID: string, jobID: number, position: number, part: number, numberOfPieces: number){
    return this.http.put(
      `${this.endpoint}/jobs/update_pieces_delivered?project_id=${projectID}&job_id=${jobID}&part=${part}&pieces_delivered=${numberOfPieces}&position=${position}`,
      { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // EK/VK Service Group
  public calculateEkVk(projectID: string, jobID: number):Observable<WebserviceResponse<any>> {
    let ekObject = this.calculateEK(projectID, jobID);
    let vkObject = this.calculateVK(projectID, jobID);

    console.table(ekObject);
    console.table(vkObject);

    return ekObject;

  }

  public calculateEK(projectID: string, jobID: number):Observable<WebserviceResponse<ek>> {
    const options = new HttpParams({
      fromObject: {
        job_id: jobID,
        project_number: projectID
      }
    });
    return this.http.get<WebserviceResponse<ek>>(
        this.endpoint + '/orders/calculation_ek',
      { params: options }).pipe(
          map((response) => normalizeSingletonResponse(response)),
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }

  public calculateVK(projectID: string, jobID: number):Observable<WebserviceResponse<vk>> {
    const options = new HttpParams({
      fromObject: {
        job_id: jobID,
        project_number: projectID
      }
    });
    return this.http.get<WebserviceResponse<vk>>(
        this.endpoint + '/orders/calculation_vk',
      { params: options }).pipe(
          map((response) => normalizeSingletonResponse(response)),
          retry(3),
          catchError(this.handleError.bind(this))
    );
  }
}
